import React, { useMemo, useState } from 'react';
import photos, { GalleryCategory, Photo } from '../../images/Gallery/photos';
import TituloPagina from '../../js/Components/PageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBriefcaseMedical, faCameraRetro, faChevronLeft, faChevronRight, faImages, faLocationDot, faShieldHeart, faTooth, faXmark } from '@fortawesome/free-solid-svg-icons';

const categoryLabels: Record<GalleryCategory, string> = { consultorio: 'Consultorio', resultados: 'Resultados' };

const PhotoGallery: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<GalleryCategory | 'todo'>('todo');
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const officePhotos = useMemo(() => photos.filter((photo) => photo.category === 'consultorio'), []);
  const resultPhotos = useMemo(() => photos.filter((photo) => photo.category === 'resultados'), []);
  const galleryPhotos = useMemo<Photo[]>(() => activeCategory === 'todo' ? photos : photos.filter((photo) => photo.category === activeCategory), [activeCategory]);
  const activePhoto = activePhotoIndex !== null ? galleryPhotos[activePhotoIndex] : null;

  const closeLightbox = () => setActivePhotoIndex(null);
  const changeCategory = (category: GalleryCategory | 'todo') => { setActiveCategory(category); setActivePhotoIndex(null); };
  const showPreviousPhoto = () => setActivePhotoIndex((currentIndex) => currentIndex === null || galleryPhotos.length === 0 ? null : currentIndex === 0 ? galleryPhotos.length - 1 : currentIndex - 1);
  const showNextPhoto = () => setActivePhotoIndex((currentIndex) => currentIndex === null || galleryPhotos.length === 0 ? null : currentIndex === galleryPhotos.length - 1 ? 0 : currentIndex + 1);

  return (
    <div>
      <TituloPagina title="DentalPuente | Galería" />
      <main className="gallery-page content-wrap">
        <section className="gallery-hero" aria-labelledby="gallery-title">
          <div className="gallery-hero-content">
            <span className="dp-eyebrow">Galería Dental Puente</span>
            <h1 id="gallery-title">Consultorio, tratamientos y resultados en un solo lugar.</h1>
            <p>Explora el espacio donde atendemos a nuestros pacientes y, cuando agregues fotografías clínicas, separa automáticamente las imágenes del consultorio y los resultados por nombre de archivo.</p>
          </div>
          <aside className="gallery-hero-card" aria-label="Resumen de galería">
            <div className="gallery-hero-icon"><FontAwesomeIcon icon={faImages} /></div>
            <div><span>Fotos disponibles</span><strong>{photos.length} imágenes</strong></div>
          </aside>
        </section>

        <section className="gallery-feature-grid" aria-label="Puntos destacados">
          <article className="gallery-feature-card gallery-feature-card-primary"><FontAwesomeIcon icon={faTooth} /><div><span>Consultorio</span><h2>Espacio profesional</h2><p>Las imágenes con prefijo <strong>consultorio-</strong> aparecen como fotos del consultorio.</p></div></article>
          <article className="gallery-feature-card"><FontAwesomeIcon icon={faBriefcaseMedical} /><div><span>Resultados</span><h2>Casos y trabajos dentales</h2><p>Las imágenes con prefijo <strong>results-</strong> o sin prefijo se muestran como resultados.</p></div></article>
          <article className="gallery-feature-card"><FontAwesomeIcon icon={faLocationDot} /><div><span>Guadalajara</span><h2>DentalPuente</h2><p>Una galería ordenada, visual y lista para crecer sin tocar código cada vez.</p></div></article>
        </section>

        <section className="gallery-showcase" aria-labelledby="gallery-showcase-title">
          <div className="gallery-section-header">
            <div><span className="dp-eyebrow">Recorrido visual</span><h2 id="gallery-showcase-title">Fotos de DentalPuente</h2></div>
            <span className="gallery-count-pill"><FontAwesomeIcon icon={faCameraRetro} />{galleryPhotos.length} fotos</span>
          </div>

          <div className="gallery-filter-tabs" role="tablist" aria-label="Filtrar galería">
            <button type="button" className={activeCategory === 'todo' ? 'active' : ''} onClick={() => changeCategory('todo')} aria-pressed={activeCategory === 'todo'}>Todo <span>{photos.length}</span></button>
            <button type="button" className={activeCategory === 'consultorio' ? 'active' : ''} onClick={() => changeCategory('consultorio')} aria-pressed={activeCategory === 'consultorio'}>Consultorio <span>{officePhotos.length}</span></button>
            <button type="button" className={activeCategory === 'resultados' ? 'active' : ''} onClick={() => changeCategory('resultados')} aria-pressed={activeCategory === 'resultados'}>Resultados <span>{resultPhotos.length}</span></button>
          </div>

          {galleryPhotos.length > 0 ? (
            <div className="gallery-masonry-grid">
              {galleryPhotos.map((photo, index) => (
                <button type="button" key={`${photo.filename}-${index}`} className={`gallery-photo-card gallery-photo-card-${(index % 5) + 1}`} onClick={() => setActivePhotoIndex(index)} aria-label={`Abrir foto ${index + 1} de la galería`}>
                  <img src={photo.src} alt={photo.alt} loading="lazy" />
                  <span className="gallery-category-badge">{categoryLabels[photo.category]}</span>
                  <span className="gallery-photo-overlay"><span>Ver foto</span><FontAwesomeIcon icon={faArrowRight} /></span>
                </button>
              ))}
            </div>
          ) : (
            <div className="gallery-empty-state"><FontAwesomeIcon icon={faShieldHeart} /><strong>No hay fotos para esta categoría.</strong><span>Agrega imágenes en src/images/Gallery y vuelve a compilar el proyecto.</span></div>
          )}
        </section>
      </main>

      {activePhoto && activePhotoIndex !== null && (
        <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label="Vista ampliada de la foto">
          <button type="button" className="gallery-lightbox-backdrop" onClick={closeLightbox} aria-label="Cerrar galería" />
          <div className="gallery-lightbox-panel">
            <div className="gallery-lightbox-toolbar"><span>{categoryLabels[activePhoto.category]} · Foto {activePhotoIndex + 1} de {galleryPhotos.length}</span><button type="button" onClick={closeLightbox} aria-label="Cerrar imagen"><FontAwesomeIcon icon={faXmark} /></button></div>
            <button type="button" className="gallery-lightbox-arrow gallery-lightbox-arrow-left" onClick={showPreviousPhoto} aria-label="Foto anterior"><FontAwesomeIcon icon={faChevronLeft} /></button>
            <img src={activePhoto.src} alt={activePhoto.alt} />
            <button type="button" className="gallery-lightbox-arrow gallery-lightbox-arrow-right" onClick={showNextPhoto} aria-label="Foto siguiente"><FontAwesomeIcon icon={faChevronRight} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
